<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import tenantService from "@/services/super-admin/tenantService";
import { toast } from "@/utils/toast";

const props = defineProps({
  search: { type: String, default: "" },
  statusFilter: { type: String, default: "" },
});

const router = useRouter();
const rows = ref([]);
const loading = ref(false);
const actionTenantId = ref(null);

const columns = [
  { title: "Company", dataIndex: "company_name", key: "company_name" },
  { title: "Workspace", dataIndex: "workspace_url", key: "workspace_url" },
  { title: "Database", dataIndex: "database_name", key: "database_name" },
  { title: "Provisioning", dataIndex: "provisioning_status", key: "provisioning_status" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "Created", dataIndex: "created_at", key: "created_at" },
  { title: "", key: "action" },
];

const filteredRows = computed(() => {
  const q = props.search.trim().toLowerCase();
  if (!q) return rows.value;

  return rows.value.filter((row) => {
    const haystack = [
      row.company_name,
      row.slug,
      row.api_domain,
      row.workspace_url,
      row.database_name,
      row.id,
      String(row.id || "").replace(/-/g, ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(q);
  });
});

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

async function copyText(value, label = "Value") {
  const text = String(value || "").trim();
  if (!text) {
    toast("Nothing to copy.", "warning");
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
    toast(`${label} copied.`, "success");
  } catch (_) {
    toast(`Could not copy ${label.toLowerCase()}.`, "error");
  }
}

function provisioningBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "badge-soft-success";
  if (normalized === "migrating" || normalized === "pending") return "badge-soft-warning";
  if (normalized === "suspended") return "badge-soft-danger";
  return "badge-soft-secondary";
}

function statusBadgeClass(status) {
  return String(status || "").toLowerCase() === "active"
    ? "badge-soft-success"
    : "badge-soft-danger";
}

async function loadTenants() {
  loading.value = true;
  try {
    const params = { per_page: 100 };
    if (props.statusFilter) params.status = props.statusFilter;

    const res = await tenantService.listTenants(params);
    rows.value = (res?.data?.data || []).map((tenant) => ({
      key: tenant.id,
      ...tenant,
    }));
  } catch (e) {
    rows.value = [];
    toast(e?.response?.data?.message || "Failed to load workspaces.", "error");
  } finally {
    loading.value = false;
  }
}

async function withAction(tenantId, fn, successMessage) {
  actionTenantId.value = tenantId;
  try {
    const res = await fn();
    toast(res?.data?.message || successMessage, "success");
    await loadTenants();
  } catch (e) {
    toast(e?.response?.data?.message || "Action failed.", "error");
  } finally {
    actionTenantId.value = null;
  }
}

function suspendTenant(record) {
  return withAction(
    record.id,
    () => tenantService.suspendTenant(record.id),
    "Workspace suspended."
  );
}

function activateTenant(record) {
  return withAction(
    record.id,
    () => tenantService.activateTenant(record.id),
    "Workspace activated."
  );
}

function runMigrations(record) {
  return withAction(
    record.id,
    () => tenantService.runTenantMigrations(record.id),
    "Tenant schema migrations completed."
  );
}

function openCompanyDetails(record) {
  if (record.company_details_url) {
    router.push(record.company_details_url);
    return;
  }

  if (record.legacy_company_id) {
    router.push(`/super-admin/companies-details/${record.legacy_company_id}`);
  }
}

function openMigrationCenter(record) {
  const companyId = record.legacy_company_id;
  if (!companyId) {
    toast("This workspace is not linked to a central company record.", "warning");
    return;
  }

  router.push(`/super-admin/migration-center?company_id=${companyId}`);
}

defineExpose({ reload: loadTenants });

onMounted(loadTenants);

watch(
  () => props.statusFilter,
  () => {
    loadTenants();
  }
);
</script>

<template>
  <div v-if="loading" class="p-4 text-muted small">Loading workspaces...</div>

  <a-table
    v-else
    class="table datatable thead-light"
    :columns="columns"
    :data-source="filteredRows"
    :pagination="{ pageSize: 20 }"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'company_name'">
        <div>
          <h6 class="fw-medium mb-0">{{ record.company_name || "—" }}</h6>
          <small class="text-muted">{{ record.slug || "—" }}</small>
          <div v-if="record.legacy_company_id" class="text-muted small">
            Central company #{{ record.legacy_company_id }}
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'workspace_url'">
        <div>
          <a
            v-if="record.workspace_url"
            :href="record.workspace_url"
            target="_blank"
            rel="noopener noreferrer"
            class="d-block"
          >
            {{ record.workspace_url }}
          </a>
          <span v-else class="text-muted">—</span>
          <small v-if="record.api_domain" class="text-muted d-block">{{ record.api_domain }}</small>
        </div>
      </template>

      <template v-else-if="column.key === 'database_name'">
        <div class="d-flex align-items-start gap-1" style="max-width: 280px">
          <code
            class="small text-break mb-0"
            :title="record.database_name || ''"
          >
            {{ record.database_name || "—" }}
          </code>
          <button
            v-if="record.database_name"
            type="button"
            class="btn btn-sm btn-white flex-shrink-0"
            title="Copy database name"
            @click="copyText(record.database_name, 'Database name')"
          >
            <i class="ti ti-copy"></i>
          </button>
        </div>
        <small v-if="record.id" class="text-muted d-block mt-1">
          Tenant ID:
          <button
            type="button"
            class="btn btn-link btn-sm p-0 align-baseline"
            title="Copy tenant ID"
            @click="copyText(record.id, 'Tenant ID')"
          >
            {{ record.id }}
          </button>
        </small>
      </template>

      <template v-else-if="column.key === 'provisioning_status'">
        <span :class="['badge', provisioningBadgeClass(record.provisioning_status), 'badge-xs']">
          {{ record.provisioning_status || "—" }}
        </span>
      </template>

      <template v-else-if="column.key === 'status'">
        <span :class="['badge', statusBadgeClass(record.status), 'badge-xs']">
          {{ record.status || "—" }}
        </span>
      </template>

      <template v-else-if="column.key === 'created_at'">
        {{ formatDate(record.created_at) }}
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex align-items-center flex-wrap gap-1">
          <a
            v-if="record.workspace_url"
            :href="record.workspace_url"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-sm btn-white"
            title="Open workspace"
          >
            <i class="ti ti-external-link"></i>
          </a>
          <button
            v-if="record.legacy_company_id"
            type="button"
            class="btn btn-sm btn-white"
            title="Company details"
            @click="openCompanyDetails(record)"
          >
            <i class="ti ti-building"></i>
          </button>
          <button
            v-if="record.legacy_company_id"
            type="button"
            class="btn btn-sm btn-white"
            title="Migration Center"
            @click="openMigrationCenter(record)"
          >
            <i class="ti ti-database-import"></i>
          </button>
          <button
            v-if="String(record.provisioning_status).toLowerCase() !== 'suspended'"
            type="button"
            class="btn btn-sm btn-white"
            title="Suspend workspace"
            :disabled="actionTenantId === record.id"
            @click="suspendTenant(record)"
          >
            <i class="ti ti-player-pause"></i>
          </button>
          <button
            v-else
            type="button"
            class="btn btn-sm btn-white"
            title="Activate workspace"
            :disabled="actionTenantId === record.id"
            @click="activateTenant(record)"
          >
            <i class="ti ti-player-play"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-white"
            title="Run tenant schema migrations"
            :disabled="actionTenantId === record.id"
            @click="runMigrations(record)"
          >
            <i class="ti ti-database-cog"></i>
          </button>
        </div>
      </template>
    </template>
  </a-table>
</template>
